import React, { useState } from 'react';
import { FaRupeeSign, FaCalendar, FaInfoCircle, FaChartLine, FaShieldAlt, FaQuestionCircle } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';

const MoratoriumCalculator = () => {
  const [loanAmount, setLoanAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [loanTenure, setLoanTenure] = useState('');
  const [moratoriumPeriod, setMoratoriumPeriod] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const calculateMoratorium = () => {
    if (!loanAmount || !interestRate || !loanTenure || !moratoriumPeriod) {
      setError('Please fill in all required fields');
      return;
    }

    const P = parseFloat(loanAmount);
    const r = parseFloat(interestRate) / 100 / 12;
    const n = parseFloat(loanTenure) * 12;
    const moratoriumMonths = parseFloat(moratoriumPeriod);

    if (isNaN(P) || isNaN(r) || isNaN(n) || isNaN(moratoriumMonths)) {
      setError('Please enter valid numbers');
      return;
    }

    // Calculate EMI without moratorium
    const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);

    // Calculate interest during moratorium
    const interestDuringMoratorium = P * r * moratoriumMonths;

    // Calculate new loan amount after moratorium
    const newLoanAmount = P + interestDuringMoratorium;

    // Calculate new EMI after moratorium
    const newEMI = (newLoanAmount * r * Math.pow(1 + r, n - moratoriumMonths)) / (Math.pow(1 + r, n - moratoriumMonths) - 1);

    const resultData = {
      originalEMI: emi.toFixed(2),
      interestDuringMoratorium: interestDuringMoratorium.toFixed(2),
      newLoanAmount: newLoanAmount.toFixed(2),
      newEMI: newEMI.toFixed(2),
      totalInterest: (interestDuringMoratorium + (newEMI * (n - moratoriumMonths) - newLoanAmount)).toFixed(2)
    };

    setResult(resultData);
    setError('');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const chartOptions = {
    chart: {
      type: 'line',
      height: 350,
      toolbar: {
        show: false
      }
    },
    colors: ['#4F46E5', '#8B5CF6'],
    stroke: {
      width: 3,
      curve: 'smooth'
    },
    xaxis: {
      categories: Array.from({ length: result ? (parseFloat(loanTenure) * 12) : 0 }, (_, i) => i + 1),
      title: {
        text: 'Months'
      }
    },
    yaxis: {
      title: {
        text: 'Amount (₹)'
      }
    },
    legend: {
      position: 'top'
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
          }).format(val);
        }
      }
    }
  };

  const chartSeries = result ? [
    {
      name: 'Original EMI',
      data: Array.from({ length: result ? (parseFloat(loanTenure) * 12) : 0 }, (_, i) => 
        i < parseFloat(moratoriumPeriod) ? 0 : parseFloat(result.originalEMI)
      )
    },
    {
      name: 'New EMI',
      data: Array.from({ length: result ? (parseFloat(loanTenure) * 12) : 0 }, (_, i) => 
        i < parseFloat(moratoriumPeriod) ? 0 : parseFloat(result.newEMI)
      )
    }
  ] : [];

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-purple-100 min-h-screen p-8">
      <Helmet>
        <title>Moratorium Calculator - Loan Payment Calculator</title>
        <meta name="description" content="Calculate your loan payments during and after a moratorium period." />
        <meta property="og:title" content="Moratorium Calculator - Loan Payment Calculator" />
        <meta property="og:description" content="Calculate your loan payments during and after a moratorium period." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <h2 className="text-4xl font-bold text-gray-800 flex items-center">
            <FaChartLine className="mr-3 text-4xl text-indigo-600" />
            Moratorium Calculator
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-2xl shadow-md">
            <label className="block text-indigo-700 text-sm font-bold mb-3">
              <FaRupeeSign className="inline-block mr-2 text-indigo-500" />
              Loan Amount (₹)
            </label>
            <div className="relative">
              <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-400" />
              <input
                type="number"
                value={loanAmount}
                onChange={(e) => setLoanAmount(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-300"
                placeholder="Enter loan amount"
                min="0"
              />
            </div>
          </div>

          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-2xl shadow-md">
            <label className="block text-indigo-700 text-sm font-bold mb-3">
              <FaChartLine className="inline-block mr-2 text-indigo-500" />
              Interest Rate (% p.a.)
            </label>
            <input
              type="number"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              className="w-full p-3 border border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-300"
              placeholder="Enter interest rate"
              min="0"
              step="0.01"
            />
          </div>

          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-2xl shadow-md">
            <label className="block text-indigo-700 text-sm font-bold mb-3">
              <FaCalendar className="inline-block mr-2 text-indigo-500" />
              Loan Tenure (Years)
            </label>
            <input
              type="number"
              value={loanTenure}
              onChange={(e) => setLoanTenure(e.target.value)}
              className="w-full p-3 border border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-300"
              placeholder="Enter loan tenure"
              min="0"
            />
          </div>

          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-2xl shadow-md">
            <label className="block text-indigo-700 text-sm font-bold mb-3">
              <FaShieldAlt className="inline-block mr-2 text-indigo-500" />
              Moratorium Period (Months)
            </label>
            <input
              type="number"
              value={moratoriumPeriod}
              onChange={(e) => setMoratoriumPeriod(e.target.value)}
              className="w-full p-3 border border-indigo-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-300"
              placeholder="Enter moratorium period"
              min="0"
              max="12"
            />
          </div>
        </div>

        <button
          onClick={calculateMoratorium}
          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-red-600 font-bold py-4 px-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 mb-10"
        >
          <FaChartLine className="inline-block mr-2" />
          Calculate Moratorium Impact
        </button>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-6 py-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {result && (
          <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaChartLine className="mr-3 text-indigo-600" />
              Payment Comparison Graph
            </h2>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <Chart options={chartOptions} series={chartSeries} type="line" height={350} />
            </div>
          </div>
        )}

        {result && (
          <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaInfoCircle className="mr-3 text-indigo-600" />
              Moratorium Impact Details
            </h2>
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Original EMI</span>
                <span className="font-semibold text-indigo-600">
                  {formatCurrency(result.originalEMI)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Interest During Moratorium</span>
                <span className="font-semibold text-indigo-600">
                  {formatCurrency(result.interestDuringMoratorium)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">New Loan Amount</span>
                <span className="font-semibold text-indigo-600">
                  {formatCurrency(result.newLoanAmount)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">New EMI After Moratorium</span>
                <span className="font-semibold text-indigo-600">
                  {formatCurrency(result.newEMI)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Interest Payment</span>
                <span className="font-semibold text-indigo-600">
                  {formatCurrency(result.totalInterest)}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaInfoCircle className="mr-3 text-indigo-600" />
            Benefits of Moratorium
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-indigo-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4 text-indigo-700">Financial Relief</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Temporary suspension of EMI payments</li>
                <li>Helps manage cash flow during emergencies</li>
                <li>No penalty for not making payments</li>
                <li>Flexibility in payment schedule</li>
              </ul>
            </div>
            <div className="p-6 bg-indigo-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4 text-indigo-700">Long-term Benefits</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Maintains credit score</li>
                <li>Prevents loan default</li>
                <li>Helps in financial planning</li>
                <li>Provides breathing room for recovery</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaQuestionCircle className="mr-3 text-indigo-600" />
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-indigo-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-indigo-700">What is a loan moratorium?</h3>
              <p className="text-gray-600">
                A loan moratorium is a temporary suspension of loan payments granted by lenders during financial emergencies. During this period, borrowers are not required to make their regular EMI payments.
              </p>
            </div>
            <div className="bg-indigo-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-indigo-700">How does moratorium affect my loan?</h3>
              <p className="text-gray-600">
                During moratorium, interest continues to accrue on your loan. This may lead to an increase in your loan tenure and EMI amount after the moratorium period ends.
              </p>
            </div>
            <div className="bg-indigo-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-indigo-700">Will my credit score be affected?</h3>
              <p className="text-gray-600">
                No, your credit score will not be negatively affected during the moratorium period as it is a formal arrangement between you and your lender.
              </p>
            </div>
            <div className="bg-indigo-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-indigo-700">Can I make payments during moratorium?</h3>
              <p className="text-gray-600">
                Yes, you can make voluntary payments during the moratorium period. Any payments made will reduce your outstanding principal and interest.
              </p>
            </div>
            <div className="bg-indigo-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-indigo-700">What happens after moratorium ends?</h3>
              <p className="text-gray-600">
                After the moratorium period ends, you'll need to resume your regular EMI payments. The EMI amount and loan tenure may be adjusted based on the accumulated interest.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaInfoCircle className="mr-3 text-indigo-600" />
            How to Use
          </h2>
          <div className="space-y-6">
            <div className="bg-indigo-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-indigo-700">Step 1: Enter Your Loan Details</h3>
              <p className="text-gray-600">
                Fill in your loan amount, interest rate, and loan tenure in years.
              </p>
            </div>
            <div className="bg-indigo-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-indigo-700">Step 2: Set Moratorium Period</h3>
              <p className="text-gray-600">
                Enter the number of months you wish to suspend your EMI payments.
              </p>
            </div>
            <div className="bg-indigo-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-indigo-700">Step 3: Calculate Impact</h3>
              <p className="text-gray-600">
                Click the "Calculate Moratorium Impact" button to see how the moratorium will affect your loan payments.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaChartLine className="mr-3 text-indigo-600" />
            Key Features
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 bg-indigo-50 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-indigo-700">Payment Comparison</h3>
              <p className="text-gray-600">
                Visual comparison of original and new EMI payments
              </p>
            </div>
            <div className="p-6 bg-indigo-50 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-indigo-700">Detailed Breakdown</h3>
              <p className="text-gray-600">
                Comprehensive view of all financial impacts
              </p>
            </div>
            <div className="p-6 bg-indigo-50 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-indigo-700">Interactive Graph</h3>
              <p className="text-gray-600">
                Interactive visualization of payment patterns
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoratoriumCalculator;
